/**
 * The shape of statio.dev/tutorials: sections above topics.
 *
 * A section is a hub page with its own URL; a topic is a heading inside one.
 * The sidebar, the index, the hub pages, the prerender list and the sitemap all
 * read this file, so adding a section is editing one array.
 *
 * It is deliberately free of `import.meta.glob`: vite-plugins/routes.ts imports
 * it from Node when the config loads, and src/content/index.ts re-exports it
 * for the app.
 */

/** The groups inside a section, in sidebar order. */
export const TUTORIAL_TOPICS = [
  { id: 'basics', label: 'Start here: the basics' },
  { id: 'clients', label: 'Connect AI clients' },
  { id: 'build', label: 'Build MCP servers' },
  { id: 'attacks', label: 'Know the attacks' },
  { id: 'access', label: 'Control agent access' },
  { id: 'standards', label: 'Frameworks and standards' },
  { id: 'policies', label: 'Write policies with Cedar' },
  { id: 'decisions', label: 'Choose your setup' },
] as const;

export type TutorialTopic = (typeof TUTORIAL_TOPICS)[number]['id'];

export interface TutorialSection {
  id: string;
  /** The h1 on the hub and the sidebar heading. */
  label: string;
  /** The hub page lives at /tutorials/<hub>. */
  hub: string;
  /** Topics in this section, in the order they appear on the hub. */
  topics: readonly TutorialTopic[];
  /** What is in here, in two or three sentences. The first one is the card copy. */
  intro: string;
  /** The <title>, without the " · Statio" suffix routes.ts adds. */
  metaTitle: string;
  metaDescription: string;
  /** Show the per-language grid at the top of the hub (the Build section). */
  languageGrid?: true;
}

export const TUTORIAL_SECTIONS: readonly TutorialSection[] = [
  {
    id: 'basics',
    label: 'Start here',
    hub: 'mcp-basics',
    topics: ['basics'],
    intro:
      'MCP is the protocol an AI client uses to call tools that run somewhere else. These pages define the words the rest of the tutorials assume: server, client, tool, agent, and how all of it differs from an ordinary API. Read them first if any of those are new.',
    metaTitle: 'MCP basics: servers, clients and agents explained',
    metaDescription:
      'Plain-English explainers for MCP: what an MCP server is, how it differs from an API, and what an AI agent actually does. The place to start.',
  },
  {
    id: 'clients',
    label: 'Connect AI clients',
    hub: 'connect-ai-clients-to-mcp',
    topics: ['clients'],
    intro:
      'Setup for the clients people actually run, Claude Code and Cursor among them. Each page names the config file, gives the exact JSON, and shows how to confirm the server answered. Every step was run against the versions printed at the top of the page.',
    metaTitle: 'Connect AI clients to MCP servers',
    metaDescription:
      'Tested setup guides for adding MCP servers to Claude Code, Cursor and other AI clients: the config file, the JSON, and how to check the connection.',
  },
  {
    id: 'build',
    label: 'Build MCP servers',
    hub: 'build-mcp-servers',
    topics: ['build'],
    intro:
      'How to write an MCP server yourself, one language at a time, and how to generate one from an OpenAPI spec when the API already exists. Each guide ends with a working server that a real client can call. Pick a language below.',
    metaTitle: 'Build an MCP server: Python, TypeScript and more',
    metaDescription:
      'Write your own MCP server step by step in Python, TypeScript, Go, Rust and more, or generate one from an OpenAPI spec. Every guide ends with a server a client can call.',
    languageGrid: true,
  },
  {
    id: 'security',
    label: 'Agent security',
    hub: 'ai-agent-security',
    topics: ['attacks', 'access', 'standards'],
    intro:
      'How AI agents get attacked, and the controls that hold. Prompt injection, tool poisoning and rug pulls on one side; least privilege, agent identity, egress limits and audit logs on the other. The standards pages put the same ground in OWASP, MITRE ATLAS and SOC 2 language, for when a reviewer asks.',
    metaTitle: 'AI agent security: attacks, controls and standards',
    metaDescription:
      'Prompt injection, tool poisoning, rug pulls and excessive agency, with the access controls that stop them and how they map to OWASP, MITRE ATLAS and SOC 2.',
  },
  {
    id: 'policies',
    label: 'Agent policies',
    hub: 'cedar-agent-policies',
    topics: ['policies'],
    intro:
      'Cedar is a policy language for deciding who may do what, and these pages apply it to AI agents and the tools they call. They cover the syntax, the entities a policy talks about, and how to test a policy before it decides anything real. Set up access first if no agent of yours has permissions yet.',
    metaTitle: 'Write agent policies in Cedar',
    metaDescription:
      'Cedar policies for AI agents: the syntax, the entities a policy refers to, and how to test a policy before it decides which tools an agent may call.',
  },
  {
    id: 'decisions',
    label: 'Choose your setup',
    hub: 'choose-mcp-setup',
    topics: ['decisions'],
    intro:
      'The decisions you make once and live with: local or remote servers, API keys or OAuth, a gateway or direct connections. Each page gives the tradeoff and then says which way to go. Finding the servers already running on your machines is in here too, because it usually comes first.',
    metaTitle: 'Choose your MCP setup: local or remote, keys or OAuth',
    metaDescription:
      'Comparisons for the MCP decisions that stick: local or remote servers, API keys or OAuth, gateway or direct, and how to find the servers already running.',
  },
];

export const TUTORIALS_PATH = '/tutorials';

export const tutorialPath = (slug: string) => `${TUTORIALS_PATH}/${slug}`;

export const sectionPath = (section: TutorialSection) => `${TUTORIALS_PATH}/${section.hub}`;

export const sectionOfTopic = (topic: TutorialTopic) => TUTORIAL_SECTIONS.find((s) => s.topics.includes(topic));

export const topicLabel = (topic: TutorialTopic) => TUTORIAL_TOPICS.find((t) => t.id === topic)?.label;

/**
 * A tutorial's `tested` line is either what the steps ran against ("Claude
 * Code 2.1 on macOS 15") or, for explainers, what the claims were checked
 * against ("Checked against OWASP ..."). Split it so the page never prints
 * "Tested with Checked against".
 */
export function testedParts(tested: string): { label: string; value: string } {
  const checked = /^checked against\s+/i.exec(tested);
  if (checked) return { label: 'Checked against', value: tested.slice(checked[0].length) };
  return { label: 'Tested with', value: tested };
}
